/**
 * Send to MDI Dialog Component
 * 
 * Dialog for sending a completed patient intake to MDI for async telehealth review.
 * Creates an MDI case from the questionnaire response and submits to MDI API.
 */

import React, { useState, useEffect } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Alert, AlertDescription } from '@/components/ui/alert'
import {
  Stethoscope,
  Loader2,
  CheckCircle2,
  AlertCircle,
  Send,
  User,
  FileText,
} from 'lucide-react'
import { useToast } from '@/hooks/use-toast'
import { mdiService } from '@/services/mdiService'

interface SendToMdiDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  patientId: string
  patientName: string
  questionnaireResponseId: string
  intakeType: string
  program?: string
  clinicId: string
  onSuccess?: (caseId: string) => void
}

// Program options must match backend values: weightwise, glp1, trt, hrt, general
const programOptions = [
  { value: 'weightwise', label: 'WeightWise' },
  { value: 'glp1', label: 'GLP-1 Weight Management' },
  { value: 'trt', label: 'Testosterone Replacement (TRT)' },
  { value: 'hrt', label: 'Hormone Replacement (HRT)' },
  { value: 'general', label: 'General Consultation' },
]

// Map intake therapy categories to MDI program values
const therapyCategoryToProgramMap: Record<string, string> = {
  'trt': 'trt',
  'mens-health': 'trt',
  'mens_health': 'trt',
  'hrt': 'hrt',
  'womens-health': 'hrt',
  'womens_health': 'hrt',
  'weight-loss': 'weightwise',
  'weight_loss': 'weightwise',
  'weightwise': 'weightwise',
  'glp1': 'glp1',
  'glp-1': 'glp1',
  'general': 'general',
}

export const SendToMdiDialog: React.FC<SendToMdiDialogProps> = ({
  open,
  onOpenChange,
  patientId,
  patientName,
  questionnaireResponseId,
  intakeType,
  program: initialProgram,
  clinicId,
  onSuccess,
}) => {
  const { toast } = useToast()
  const [isSubmitting, setIsSubmitting] = useState(false)
  
  // Map the intake type or initial program to a valid MDI program value
  const getMappedProgram = (value?: string): string => {
    if (!value) return ''
    const lowerValue = value.toLowerCase()
    return therapyCategoryToProgramMap[lowerValue] || lowerValue
  }
  
  const [selectedProgram, setSelectedProgram] = useState(getMappedProgram(initialProgram || intakeType))
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)
  const [createdCaseId, setCreatedCaseId] = useState<string | null>(null)
  
  // Update selected program when dialog opens with a new intake
  useEffect(() => {
    if (open) {
      const mapped = getMappedProgram(initialProgram || intakeType)
      setSelectedProgram(mapped)
      setError(null)
      setSuccess(false)
      setCreatedCaseId(null)
    }
  }, [open, initialProgram, intakeType])

  const handleSubmit = async () => {
    if (!selectedProgram) {
      setError('Please select a program')
      return
    }

    setIsSubmitting(true)
    setError(null)

    try {
      const response = await mdiService.createCase({
        patientId,
        questionnaireResponseId,
        program: selectedProgram,
        source: 'clinic_portal',  // Source is where the request originates from
        clinicId,
      })

      if (response.success && response.data) {
        setSuccess(true)
        setCreatedCaseId(response.data.id)
        toast({
          title: 'Case submitted to MDI',
          description: 'The patient intake has been sent for async telehealth review.',
        })
        onSuccess?.(response.data.id)
      } else {
        setError(response.message || 'Failed to create MDI case')
      }
    } catch (err) {
      console.error('Error creating MDI case:', err)
      setError('Failed to submit to MDI. Please try again.')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleClose = () => {
    if (!isSubmitting) {
      setError(null)
      setSuccess(false)
      setCreatedCaseId(null)
      onOpenChange(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md max-h-[90vh] flex flex-col bg-[#0A0F1A] border border-white/10 text-white">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle className="flex items-center gap-2 text-white">
            <Stethoscope className="w-5 h-5 text-indigo-400" />
            Send to MDI Telehealth
          </DialogTitle>
          <DialogDescription className="text-white/60">
            Submit this intake for async review by an MDI clinician
          </DialogDescription>
        </DialogHeader>

        {success ? (
          <div className="py-6 space-y-4 flex-1 overflow-y-auto">
            <div className="flex flex-col items-center text-center">
              <div className="w-16 h-16 rounded-full bg-green-500/10 flex items-center justify-center mb-4">
                <CheckCircle2 className="w-8 h-8 text-green-400" />
              </div>
              <h3 className="text-lg font-semibold text-white mb-2">
                Successfully Submitted
              </h3>
              <p className="text-sm text-white/60">
                The intake has been sent to MDI for review. You'll be notified when the 
                clinician completes their assessment.
              </p>
              {createdCaseId && (
                <Badge className="mt-4 bg-indigo-500/10 text-indigo-400 border-indigo-500/20">
                  Case ID: {createdCaseId}
                </Badge>
              )}
            </div>
            <DialogFooter className="flex-shrink-0">
              <Button
                onClick={handleClose}
                className="w-full bg-indigo-500 hover:bg-indigo-600 text-white"
              >
                Done
              </Button>
            </DialogFooter>
          </div>
        ) : (
          <>
            <div className="space-y-4 py-4 flex-1 overflow-y-auto">
              {/* Patient Info */}
              <div className="p-3 rounded-lg bg-white/[0.02] border border-white/[0.06]">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-blue-500/10">
                    <User className="w-4 h-4 text-blue-400" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-white">{patientName}</p>
                    <p className="text-xs text-white/50">Patient</p>
                  </div>
                </div>
              </div>

              {/* Intake Info */}
              <div className="p-3 rounded-lg bg-white/[0.02] border border-white/[0.06]">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-amber-500/10">
                    <FileText className="w-4 h-4 text-amber-400" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-white">{intakeType}</p>
                    <p className="text-xs text-white/50">Intake Type</p>
                  </div>
                </div>
              </div>

              {/* Program Display (Read-only) */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-white/80">
                  Program
                </label>
                <div className="w-full p-3 rounded-md bg-white/[0.02] border border-white/10 text-white">
                  {programOptions.find(p => p.value === selectedProgram)?.label || selectedProgram}
                </div>
              </div>

              {/* Info Alert */}
              <Alert className="bg-indigo-500/10 border-indigo-500/20">
                <Stethoscope className="h-4 w-4 text-indigo-400" />
                <AlertDescription className="text-indigo-200 text-sm">
                  The MDI clinician will review this intake asynchronously. 
                  Once completed, encounter notes and any prescriptions will 
                  appear in the patient's record.
                </AlertDescription>
              </Alert>

              {/* Error */}
              {error && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}
            </div>

            <DialogFooter className="gap-2 flex-shrink-0 border-t border-white/10 pt-4">
              <Button
                variant="ghost"
                onClick={handleClose}
                disabled={isSubmitting}
                className="text-white/60 hover:text-white hover:bg-white/5"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={isSubmitting || !selectedProgram}
                className="bg-indigo-500 hover:bg-indigo-600 text-white"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Send to MDI
                  </>
                )}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}

export default SendToMdiDialog







